import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type {
  Stack,
  FxEnvironmentsResponse,
  CloneCompanyAndWorkflowsRequest,
  CloneCompanyAndWorkflowsResponse,
  CloneCompanyWorkflow,
  CloneCompanyWorkflowDetail,
  WorkflowStatus,
  ProdToLowerConfigView,
  ProdToLowerRequest,
  ProdToLowerRunView,
} from './types';
import type { KaminoApi } from './KaminoApi';

/**
 * Real implementation of the Kamino API that calls the backend for clone,
 * environment, and operation data.
 *
 * Implements KaminoApi directly (rather than extending the mock client) so
 * that a backend/config gap surfaces as real empty/error state instead of
 * silently falling back to mock data.
 * @public
 */
export class KaminoClient implements KaminoApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('kamino');
  }

  private parseWorkflowStatus(value: string | undefined): WorkflowStatus {
    switch (value) {
      case 'pending':
      case 'in_progress':
      case 'export_ready':
      case 'completed':
      case 'failed':
        return value;
      default:
        return 'pending';
    }
  }

  private mapWorkflowToListItem(
    workflow: BackendWorkflowState,
  ): CloneCompanyWorkflow {
    const dash = '—';
    return {
      id: workflow.operationId,
      operationType: workflow.operationType,
      originStackId: workflow.sourceEnvironmentId ?? '',
      originStackName: workflow.sourceEnvironmentId ?? dash,
      destinationStackId: workflow.targetEnvironmentId ?? '',
      destinationStackName: workflow.targetEnvironmentId ?? dash,
      companyId: workflow.companyId,
      newCompanyName: workflow.newCompanyName ?? workflow.companyId,
      status: this.parseWorkflowStatus(workflow.status),
      createdAt: workflow.createdAt,
      updatedAt: workflow.updatedAt,
    };
  }

  private mapWorkflowToDetail(
    workflow: BackendWorkflowState,
  ): CloneCompanyWorkflowDetail {
    const status = this.parseWorkflowStatus(workflow.status);
    const isExport = workflow.operationType === 'export';
    const isClone = workflow.operationType === 'clone';
    const terminalStatuses: WorkflowStatus[] = isExport
      ? ['export_ready', 'completed', 'failed']
      : ['completed', 'failed'];
    const hasFinalStatus = terminalStatuses.includes(status);

    let executionStatus: WorkflowStatus = 'pending';
    if (status === 'in_progress') {
      executionStatus = 'in_progress';
    } else if (status === 'failed') {
      executionStatus = 'failed';
    } else if (hasFinalStatus) {
      executionStatus = 'completed';
    }

    const executionStartedAt =
      status === 'pending' ? undefined : workflow.createdAt;

    const settings = workflow.settings ?? {};
    const sendEmailInvitations = Boolean(settings.sendEmailInvitations);
    const requestedWorkflowCount =
      typeof settings.workflowCount === 'number'
        ? settings.workflowCount
        : undefined;
    const requestedRecordCount =
      typeof settings.recordCount === 'number'
        ? settings.recordCount
        : undefined;
    const requestedWorkflowIds = Array.isArray(settings.workflowIds)
      ? (settings.workflowIds as string[])
      : undefined;

    let acceptedLabel = 'Import request accepted';
    if (isExport) {
      acceptedLabel = 'Export request accepted';
    } else if (isClone) {
      acceptedLabel = 'Clone request accepted';
    }
    const executionLabel = isExport ? 'Export execution' : 'Clone execution';

    return {
      ...this.mapWorkflowToListItem(workflow),
      sendEmailInvitations,
      requestedWorkflowCount,
      requestedWorkflowIds,
      requestedRecordCount,
      newCompanyId: workflow.result?.clonedCompanyId,
      initiatedBy: workflow.initiatedBy,
      error: workflow.error,
      steps: [
        {
          name: acceptedLabel,
          status: 'completed',
          startedAt: workflow.createdAt,
          completedAt: workflow.createdAt,
        },
        {
          name: executionLabel,
          status: executionStatus,
          startedAt: executionStartedAt,
          completedAt:
            hasFinalStatus && status !== 'failed'
              ? workflow.updatedAt
              : undefined,
          error: status === 'failed' ? workflow.error : undefined,
        },
      ],
    };
  }

  /**
   * Fetch the configured environments from the backend. The backend lists only
   * the environments present in config (none => empty). A backend failure is
   * treated as "no environments configured" rather than falling back to mock
   * data, so the UI never shows phantom stacks.
   */
  async getAvailableStacks(): Promise<Stack[]> {
    try {
      const baseUrl = await this.getBaseUrl();
      const response = await this.fetchApi.fetch(`${baseUrl}/environments`);
      if (!response.ok) {
        return [];
      }
      const data = (await response.json()) as Stack[];
      return data.map(env => ({ id: env.id, name: env.name }));
    } catch {
      return [];
    }
  }

  /**
   * Fetch the live fx branch environments. Unlike {@link getAvailableStacks}
   * this throws on failure: the list comes from GitHub, and an outage should
   * read as an error on the fx option rather than as "no branches are live".
   */
  async getFxEnvironments(): Promise<FxEnvironmentsResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/fx-environments`);
    if (!response.ok) {
      throw await responseError(response, 'Failed to list fx environments');
    }
    return (await response.json()) as FxEnvironmentsResponse;
  }

  /**
   * Call the backend for the "clone company and workflows" (Ironclad Bootstrap) flow. The backend proxies to the
   * harbor `/company-and-workflows-v2` endpoint, which mints the operationId
   * server-side and returns it.
   */
  async requestCloneCompanyAndWorkflows(
    request: CloneCompanyAndWorkflowsRequest,
  ): Promise<CloneCompanyAndWorkflowsResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/clone-company-and-workflows-v2`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          environmentId: request.environmentId,
          environmentToken: request.environmentToken,
          companyId: request.companyId,
          name: request.name,
          displayName: request.displayName,
          placeholderFileUrl: request.placeholderFileUrl,
          salesforceInfo: request.salesforceInfo,
          newUsers: request.newUsers,
          sendEmailInvitations: request.sendEmailInvitations,
          workflowCount: request.workflowCount,
          workflowIds: request.workflowIds,
          recordCount: request.recordCount,
        }),
      },
    );

    if (!response.ok) {
      throw await responseError(response, 'Clone request failed');
    }

    const data = await response.json();

    if (typeof data.operationId !== 'string' || data.operationId.length === 0) {
      throw new Error('Clone response did not include an operationId');
    }

    return {
      operationId: data.operationId,
      status: this.parseWorkflowStatus(
        typeof data.status === 'string' ? data.status : undefined,
      ),
      createdAt:
        typeof data.createdAt === 'string'
          ? data.createdAt
          : new Date().toISOString(),
    };
  }

  async getOperations(): Promise<CloneCompanyWorkflow[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/workflows`);

    if (!response.ok) {
      throw await responseError(response, 'Unable to fetch workflows');
    }

    const data = (await response.json()) as BackendWorkflowState[];
    return data.map(workflow => this.mapWorkflowToListItem(workflow));
  }

  async getOperationDetail(
    operationId: string,
  ): Promise<CloneCompanyWorkflowDetail> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/workflow/${encodeURIComponent(operationId)}`,
    );

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(`Operation ${operationId} not found`);
      }

      throw await responseError(response, 'Unable to fetch operation');
    }

    const data = (await response.json()) as BackendWorkflowState;
    return this.mapWorkflowToDetail(data);
  }

  async getProdToLowerConfig(): Promise<ProdToLowerConfigView> {
    return this.prodToLower(
      '/prod-to-lower/config',
      'Unable to load prod-to-lower settings',
    );
  }

  async listProdToLowerRuns(): Promise<ProdToLowerRunView[]> {
    return this.prodToLower(
      '/prod-to-lower/runs',
      'Unable to list prod-to-lower runs',
    );
  }

  async getProdToLowerRun(runId: string): Promise<ProdToLowerRunView> {
    return this.prodToLower(
      `/prod-to-lower/runs/${encodeURIComponent(runId)}`,
      'Unable to load the prod-to-lower run',
    );
  }

  async requestProdToLowerRun(
    request: ProdToLowerRequest,
  ): Promise<ProdToLowerRunView> {
    return this.prodToLower(
      '/prod-to-lower/runs',
      'Prod-to-lower request failed',
      request,
    );
  }

  async releaseProdToLowerRun(
    runId: string,
    options: { acknowledgeWarnings: boolean },
  ): Promise<ProdToLowerRunView> {
    return this.prodToLower(
      `/prod-to-lower/runs/${encodeURIComponent(runId)}/release`,
      'Release failed',
      options,
    );
  }

  async retryProdToLowerRun(runId: string): Promise<ProdToLowerRunView> {
    return this.prodToLower(
      `/prod-to-lower/runs/${encodeURIComponent(runId)}/retry`,
      'Retry failed',
      {},
    );
  }

  async cancelProdToLowerRun(runId: string): Promise<ProdToLowerRunView> {
    return this.prodToLower(
      `/prod-to-lower/runs/${encodeURIComponent(runId)}/cancel`,
      'Cancel failed',
      {},
    );
  }

  /** GET `path`, or POST `body` to it, and return the JSON answer. */
  private async prodToLower<T>(
    path: string,
    fallback: string,
    body?: unknown,
  ): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}${path}`,
      body === undefined
        ? undefined
        : {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          },
    );
    if (!response.ok) {
      throw await responseError(response, fallback);
    }
    return (await response.json()) as T;
  }
}

/**
 * The backend's view of an operation for a signed-in user. It is narrowed
 * server-side: harbor's raw result and any stored email lists never reach here.
 */
interface BackendWorkflowState {
  operationId: string;
  companyId: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  result?: { clonedCompanyId?: string };
  error?: string;
  operationType?: 'export' | 'import' | 'clone';
  sourceEnvironmentId?: string;
  targetEnvironmentId?: string;
  newCompanyName?: string;
  initiatedBy?: string;
  settings?: Record<string, unknown>;
}

/** The backend returns both service errors and Backstage error envelopes. */
async function responseError(
  response: Response,
  fallback: string,
): Promise<Error> {
  const body: unknown = await response.json().catch(() => undefined);
  const error =
    body && typeof body === 'object' && 'error' in body
      ? body.error
      : undefined;
  let message: string | undefined;
  if (typeof error === 'string') {
    message = error;
  } else if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    message = error.message;
  }
  return new Error(
    message || `${fallback}: ${response.statusText || response.status}`,
  );
}
